import React, { useMemo } from 'react';

import { Box, Checkbox } from '@mantine/core';

import { ActivityFeedFilter, FilterOption } from '@/types/activityFeed';

import styles from '../ActivityFeed.module.scss';
import { DEFAULT_FILTER_OPTIONS } from '../utils';

interface FilterMenuProps {
  selectedOptions: string[];
  setSelectedOptions: React.Dispatch<React.SetStateAction<string[]>>;
}

interface FilterOptionProps {
  option: FilterOption;
  selectedOptions: string[];
  onChange: () => void;
  isChild?: boolean;
}

export const FilterMenu: React.FC<FilterMenuProps> = ({ selectedOptions, setSelectedOptions }) => {
  const options: Record<string, FilterOption[]> = useMemo(() => {
    const newOptions: Record<string, FilterOption[]> = {};

    // Process each category
    Object.entries(DEFAULT_FILTER_OPTIONS).forEach(([category, filterOptions]) => {
      // Create a new array for this category
      newOptions[category] = filterOptions.map((option) => ({
        ...option,
        // Set checked to true if the option's id is in selectedOptions
        checked: selectedOptions.includes(option.id)
      }));
    });

    return newOptions;
  }, [selectedOptions]);

  const onCategoryCheckboxChange = (category: string) => {
    const categoryOptionIds = DEFAULT_FILTER_OPTIONS[category].map((option) => option.id);

    // Check if any options in this category are currently selected
    const hasSelectedOptions = categoryOptionIds.some((id) => selectedOptions.includes(id));

    if (hasSelectedOptions) {
      // If any are selected, uncheck them all by filtering them out
      const newSelectedOptions = selectedOptions.filter((id) => !categoryOptionIds.includes(id));
      setSelectedOptions(newSelectedOptions);
    } else {
      // If none are selected, check them all by adding all category option IDs
      const newSelectedOptions = [...selectedOptions, ...categoryOptionIds];
      setSelectedOptions(newSelectedOptions);
    }
  };

  const onChildCheckboxChange = (optionId: string) => {
    const isSelected = selectedOptions.includes(optionId);

    if (isSelected) {
      const newSelectedOptions = selectedOptions.filter((id) => id !== optionId);
      setSelectedOptions(newSelectedOptions);
    } else {
      const newSelectedOptions = [...selectedOptions, optionId];
      setSelectedOptions(newSelectedOptions);
    }
  };

  return (
    <Box className={styles.filtersMenu}>
      {Object.entries(options).map(([category, categoryOptions]) => {
        // If category has only one option, render the option directly
        if (categoryOptions.length === 1) {
          const option = categoryOptions[0];
          return (
            <div
              key={option.id}
              className={`${styles.filterOption} ${option.checked ? styles.selectedOption : ''}`}>
              <FilterOptionItem
                option={option}
                selectedOptions={selectedOptions}
                onChange={() => onChildCheckboxChange(option.id)}
                isChild={true}
              />
            </div>
          );
        }

        // Otherwise, render the category with its options
        return (
          <div key={`category-${category}`}>
            <div
              className={`${styles.filterOption} ${categoryOptions.some((opt) => opt.checked) ? styles.selectedOption : ''}`}>
              <FilterOptionItem
                option={{
                  id: category,
                  label: category,
                  checked: categoryOptions.every((opt) => opt.checked)
                }}
                selectedOptions={selectedOptions}
                onChange={() => onCategoryCheckboxChange(category)}
                isChild={false}
              />
            </div>

            {categoryOptions.length > 0 && (
              <div>
                {categoryOptions.map((option) => (
                  <div
                    key={option.id}
                    className={`${styles.filterOptionChild} ${option.checked ? styles.selectedOption : ''}`}>
                    <FilterOptionItem
                      option={option}
                      selectedOptions={selectedOptions}
                      onChange={() => onChildCheckboxChange(option.id)}
                      isChild={true}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      <div
        className={`${styles.filterOption} ${selectedOptions.length === Object.values(ActivityFeedFilter).length ? styles.selectedOption : ''}`}>
        <FilterOptionItem
          option={{
            id: 'all',
            label: 'All',
            checked: selectedOptions.length === Object.values(ActivityFeedFilter).length
          }}
          selectedOptions={selectedOptions}
          onChange={() => {
            if (selectedOptions.length === Object.values(ActivityFeedFilter).length) {
              setSelectedOptions([]);
            } else {
              setSelectedOptions(Object.values(ActivityFeedFilter));
            }
          }}
        />
      </div>
    </Box>
  );
};

const FilterOptionItem: React.FC<FilterOptionProps> = ({
  option,
  selectedOptions,
  onChange,
  isChild = false
}: FilterOptionProps) => {
  const { isChecked, isIndeterminate } = useMemo(() => {
    if (option.id === 'all') {
      return {
        isChecked: selectedOptions.length === Object.values(ActivityFeedFilter).length,
        isIndeterminate:
          selectedOptions.length > 0 &&
          selectedOptions.length < Object.values(ActivityFeedFilter).length
      };
    }

    if (!isChild) {
      const categoryOptionIds = DEFAULT_FILTER_OPTIONS[option.id]?.map((opt) => opt.id) || [];
      const selectedCategoryOptions = categoryOptionIds.filter((id) =>
        selectedOptions.includes(id)
      );

      return {
        isChecked:
          categoryOptionIds.length > 0 &&
          selectedCategoryOptions.length === categoryOptionIds.length,
        isIndeterminate:
          selectedCategoryOptions.length > 0 &&
          selectedCategoryOptions.length < categoryOptionIds.length
      };
    }

    return {
      isChecked: selectedOptions.includes(option.id),
      isIndeterminate: false
    };
  }, [isChild, option.id, selectedOptions]);

  return (
    <label className={styles.filterCheckbox}>
      <Checkbox
        checked={isChecked}
        onChange={onChange}
        mr={10}
        indeterminate={isIndeterminate}
        variant="outline"
      />
      <span className={styles.filterLabel}>{option.label}</span>
    </label>
  );
};
